import {
    IDrivingStore,
    IDrivingVehicle
} from 'components/driving/driving-interface'
import { MyTopicsData } from 'components/main/my-topics/my-topics-interface'

export enum HtmlAttr {
    ALT = 'alt',
    CHECKED = 'checked',
    CLASS = 'class',
    DISABLED = 'disabled',
    HIDDEN = 'hidden',
    HREF = 'href',
    ID = 'id',
    REQUIRED = 'required',
    SOURCE = 'src',
    STYLE = 'style',
    TAB_INDEX = 'tabindex',
    TARGET = 'target',
    TYPE = 'type'
}

export enum AriaAttr {
    CONTROLS = 'aria-controls',
    EXPANDED = 'aria-expanded',
    DESCRIBED_BY = 'aria-describedby',
    HIDDEN = 'aria-hidden',
    INVALID = 'aria-invalid',
    LABEL = 'aria-label',
    LIVE = 'aria-live'
}

export enum HTMLElementType {
    IFRAME = 'iframe',
    INPUT = 'input',
    SCRIPT = 'script',
    SELECT = 'select',
    TEXTAREA = 'textarea'
}

export enum EventType {
    BEFORE_UNLOAD = 'beforeunload',
    ANIMATION_START = 'animationstart',
    ANIMATION_END = 'animationend',
    BLUR = 'blur',
    CHANGE = 'change',
    CLICK = 'click',
    DOM_CONTENT_LOADED = 'DOMContentLoaded',
    FOCUS = 'focus',
    FOCUS_OUT = 'focusout',
    FORM_VALIDATED = 'pnFormValidated',
    FULLSCREEN_CHANGE = 'fullscreenchange',
    INPUT = 'input',
    KEYDOWN = 'keydown',
    KEYUP = 'keyup',
    LOAD = 'load',
    MOUSE_OVER = 'mouseover',
    MOUSE_ENTER = 'mouseenter',
    MOUSE_LEAVE = 'mouseleave',
    PASTE = 'paste',
    RESIZE = 'resize',
    SCROLL = 'scroll',
    SCROLL_END = 'scrollend',
    SUBMIT = 'submit',
    TOUCH_END = 'touchend',
    TOUCH_START = 'touchstart',
    VISIBILITY_CHANGE = 'visibilitychange',
    WHEEL = 'wheel',
    OPEN_RECOMMENDATION_MODAL = 'openRecommendationModal'
}

export enum InputType {
    CHECKBOX = 'checkbox',
    EMAIL = 'email',
    SUBMIT = 'submit',
    TEL = 'tel',
    URL = 'url',
    FILE = 'file'
}

export enum KeyType {
    DOWN = 'ArrowDown',
    DOWN_IE = 'Down',
    END = 'End',
    ENTER = 'Enter',
    ESC = 'Escape',
    ESC_IE = 'Esc',
    HOME = 'Home',
    LEFT = 'ArrowLeft',
    LEFT_IE = 'Left',
    RIGHT = 'ArrowRight',
    RIGHT_IE = 'Right',
    SPACE = ' ',
    TAB = 'Tab',
    UP = 'ArrowUp',
    UP_IE = 'Up'
}

/* eslint-disable no-magic-numbers */
export enum ResponseStatus {
    OK = 200,
    NOT_FOUND = 404,
    SERVER_ERROR = 500
}

export enum MutationType {
    ATTRIBUTES = 'attributes',
    CHILD_LIST = 'childList',
    SUBTREE = 'subtree'
}

export enum ComponentRenderType {
    CAROUSEL = 'carousal',
    VIDEO = 'video'
}

/* eslint-disable no-magic-numbers */
export enum PlaylistId {
    JW = 8,
    YT = 32
}

export enum CarouselType {
    LIST = 'list',
    SLIDE = 'slide'
}

export enum SlidingDrawerEvent {
    OPEN = 'SlidingDrawerOpen',
    CLOSE = 'SlidingDrawerClose',
    OPENED = 'SlidingDrawerOpenFinished',
    CLOSED = 'SlidingDrawerCloseFinished'
}

export enum PopoverEvent {
    OPEN = 'PopoverOpen',
    CLOSE = 'PopoverClose'
}

export enum StickyWrapperEvent {
    CLOSE = 'StickyVideoClose',
    MINIMIZE = 'StickyVideoMinimized',
    CHANGE_TITLE = 'StickyVideoTitleChange'
}

export enum HeaderMenuEvent {
    OPEN = 'MenuOpen',
    CLOSE = 'MenuClose'
}

export enum RecommendationEvent {
    PN_ID_PROFILE_INIT = 'pnIdProfileInit',
    SAVE_RECOMMENDATION_CATEGORIES = 'saveRecommendationCategories',
    RECOMMENDATION_CATEGORIES_SAVED_SUCCESS = 'recommendationCategoriesSavedSuccess',
    RECOMMENDATION_CATEGORIES_SAVED_ERROR = 'recommendationCategoriesSavedError'
}

export enum ListType {
    INDEX = 'index',
    CATEGORY = 'category',
    GLOBE_NEWSWIRE = 'globe_newswire',
    TRENDING = 'trending',
    RELATED_ARTICLES = 'related_articles',
    RSS = 'rss',
    PARTNER_FEED = 'partner_feed',
    HEADLINE_FEED = 'headline_feed'
}

export enum Position {
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right'
}

/**
 * Storage items
 */
export enum PostmediaStorage {
    CAR_COMPARISON,
    VEHICLE_FINDER,
    SPONSORED_VEHICLE,
    MY_TOPICS
}

/**
 * Local Storage keys
 */
export enum LocalStorageKey {
    XID = 'x-id',
    LRTOKENKEY = 'LRTokenKey',
    AUTH0TOKENKEY = 'Auth0TokenKey',
    JWT = 'lr_jwt'
}

export enum HeaderMenuNames {
    PRIMARY = 'primary-nav',
    ACCOUNT = 'account-nav'
}

/**
 * Get storage type per item
 * i.g. StorageTypes<Storages.CAR_COMPARISON> returns the type of car comparison data
 * so you can use it call generic
 *
 * Add type here if you expand the item of Postmedia session storage
 * @see SessionStore in services
 */
export type StorageTypes<T extends PostmediaStorage> =
    T extends PostmediaStorage.CAR_COMPARISON
        ? IDrivingStore<IDrivingVehicle>[] // CAR_COMPARISON
        : T extends PostmediaStorage.VEHICLE_FINDER
        ? 'open' | 'closed' | '' // VEHICLE_FINDER
        : T extends PostmediaStorage.SPONSORED_VEHICLE
        ? { ids: number[]; item?: IDrivingVehicle } // SPONSORED_VEHICLE
        : MyTopicsData[] // MY_TOPICS

/**
 * Represents the whole storage
 */
export type StorageType = { [key in PostmediaStorage]: StorageTypes<key> }

/**
 * Initial value
 */
export const initValues: StorageType = {
    [PostmediaStorage.CAR_COMPARISON]: [],
    [PostmediaStorage.VEHICLE_FINDER]: '',
    [PostmediaStorage.SPONSORED_VEHICLE]: {
        ids: []
    },
    [PostmediaStorage.MY_TOPICS]: []
}

export const focusAbleElementsSelector =
    'a, button, input, select, textarea, details, iframe, audio, video, [tabindex="0"]'

export enum SubscriptionAuthPageEventType {
    INIT_LOGIN = 'initLogin',
    INIT_PASSWORDLESS_LOGIN = 'initPasswordlessLogin',
    INIT_PASSWORDLESS_LOGIN_SUCCESS = 'initPasswordlessLoginSuccess',
    INIT_SIGN_IN_OR_SIGN_UP = 'initSignInOrSignUp',
    PN_ID_INIT = 'pnIdInit'
}
